import { Controller } from "stimulus"

export default class extends Controller {
  static targets = ["display", "editor", "input", "editButton", "saveButton", "status"]

  connect() {
    this.value = this.data.get("value") || ""
    this.savedHandler = this.handleExternalSave.bind(this)
    document.addEventListener("inline-lead-edit:saved", this.savedHandler)
  }

  disconnect() {
    document.removeEventListener("inline-lead-edit:saved", this.savedHandler)
  }

  edit(event) {
    event.preventDefault()
    if (this.busy || this.editing) return

    this.editing = true
    this.inputTarget.value = this.value
    this.element.classList.add("is-editing")
    this.displayTarget.classList.add("d-none")
    this.editButtonTarget.classList.add("d-none")
    this.editorTarget.classList.remove("d-none")
    this.clearStatus()

    window.requestAnimationFrame(() => {
      this.inputTarget.focus()
      if (this.inputTarget.select && this.data.get("editor") !== "select") this.inputTarget.select()
    })
  }

  cancel(event) {
    if (event) event.preventDefault()
    this.closeEditor()
  }

  keydown(event) {
    if (event.key === "Escape") {
      event.preventDefault()
      this.closeEditor()
      return
    }

    if (event.key !== "Enter") return
    if (this.data.get("editor") === "textarea" && !(event.metaKey || event.ctrlKey)) return

    event.preventDefault()
    this.save(event)
  }

  async save(event) {
    if (event) event.preventDefault()
    if (this.busy) return

    const nextValue = this.inputTarget.value
    if (nextValue === this.value) {
      this.closeEditor()
      return
    }

    this.setBusy(true)

    try {
      const paramName = this.data.get("paramName") || "lead"
      const response = await fetch(this.data.get("url"), {
        method: "PATCH",
        credentials: "same-origin",
        headers: {
          "Accept": "application/json",
          "Content-Type": "application/json",
          "X-CSRF-Token": this.csrfToken(),
          "X-Requested-With": "XMLHttpRequest"
        },
        body: JSON.stringify({
          [paramName]: { [this.data.get("field")]: nextValue },
          inline_context: this.data.get("context") || "lead_detail"
        })
      })

      const payload = await response.json().catch(() => ({}))

      if (!response.ok) {
        const errors = payload.errors || ["Speichern fehlgeschlagen."]
        throw new Error(errors.join(" "))
      }

      this.applyPayload(payload)
      this.closeEditor()
      this.flashSaved()

      document.dispatchEvent(new CustomEvent("inline-lead-edit:saved", {
        detail: { payload: payload, source: this.element }
      }))
    } catch (error) {
      this.setStatus(error.message || "Speichern fehlgeschlagen.", true)
    } finally {
      this.setBusy(false)
    }
  }

  handleExternalSave(event) {
    if (!event.detail || event.detail.source === this.element) return

    const payload = event.detail.payload || {}
    if (String(payload.id) !== String(this.data.get("recordId") || this.data.get("leadId"))) return
    if (String(payload.field) !== String(this.data.get("field"))) return

    this.applyPayload(payload)
  }

  applyPayload(payload) {
    const nextValue = payload.value == null ? "" : String(payload.value)
    this.value = nextValue
    this.data.set("value", nextValue)
    this.inputTarget.value = nextValue

    if (payload.display_html) {
      this.displayTarget.innerHTML = payload.display_html
    } else {
      this.displayTarget.textContent = nextValue || this.data.get("emptyText") || "-"
    }
  }

  closeEditor() {
    this.editing = false
    this.element.classList.remove("is-editing")
    this.displayTarget.classList.remove("d-none")
    this.editButtonTarget.classList.remove("d-none")
    this.editorTarget.classList.add("d-none")
    this.inputTarget.value = this.value
    this.clearStatus()
  }

  setBusy(isBusy) {
    this.busy = isBusy
    this.element.classList.toggle("is-saving", isBusy)
    if (this.hasSaveButtonTarget) this.saveButtonTarget.disabled = isBusy
    if (this.hasInputTarget) this.inputTarget.disabled = isBusy
    if (isBusy) this.setStatus("Speichert ...", false)
  }

  flashSaved() {
    this.element.classList.add("is-saved")
    window.setTimeout(() => this.element.classList.remove("is-saved"), 900)
  }

  setStatus(message, isError) {
    if (!this.hasStatusTarget) return

    this.statusTarget.textContent = message
    this.statusTarget.classList.toggle("is-error", Boolean(isError))
  }

  clearStatus() {
    if (!this.hasStatusTarget) return

    this.statusTarget.textContent = ""
    this.statusTarget.classList.remove("is-error")
  }

  csrfToken() {
    const token = document.querySelector("meta[name='csrf-token']")
    return token ? token.getAttribute("content") : ""
  }
}
